import React from 'react';
import { Olympiad, OlympiadStatus, olympiadStatusTranslate } from '../../models/Olympiad';
import { CalendarIcon as Calendar, InfoIcon as Info } from '@gr-monorepo/common/public/assets/icons';
import { SecondaryButton } from '@gr-monorepo/common/public/ui/Buttons';
import Tooltip, { TooltipPlacement } from 'features/shared/Tooltip';
import { LogClick } from 'features/shared/ClickStream';
import OlympiadInformButton from '../OlympiadInformButton/InformModal';
import * as S from './OlympiadCardStyled';

type OlympiadCardProps = {
  olympiad: Olympiad;
  showWarningMessage?: boolean;
  hideHeader?: boolean;
  showInformButton?: boolean;
  onClick?: () => void;
  'data-testid'?: string;
};

const stopProp = (e: React.MouseEvent) => e.stopPropagation();
const stopEvent = (e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const DatesStatus = ({ status, dates }: Pick<Olympiad, 'status' | 'dates'>) => (
  <S.DatesStatusWrapper>
    {status && (
      <S.OlympiadStatusBadge
        $status={status}
        data-testid={`olympiad-status-badge-${status as OlympiadStatus}`}
      >
        {olympiadStatusTranslate[status as OlympiadStatus]}
      </S.OlympiadStatusBadge>
    )}
    <S.Dates $status={status}>
      <Calendar /> <span>{dates}</span>
    </S.Dates>
  </S.DatesStatusWrapper>
);

const MinistryHintBadge = ({ hint }: { hint: string }) => (
  <S.MinistryBadgeWrap onClick={stopEvent} onMouseDown={stopEvent}>
    <Tooltip
      title={hint}
      arrow
      flip
      placement={TooltipPlacement.TopStart}
      backgroundColor="#8682AB"
      minWidth={55}
      maxWidth={300}
      style={{ padding: 12, fontSize: 12 }}
    >
      <S.MinistryBadge $status={OlympiadStatus.passed}>
        <Info /> Перечень Минпросвещения России
      </S.MinistryBadge>
    </Tooltip>
  </S.MinistryBadgeWrap>
);

const InsideActions = ({
  url,
  popupText,
  showInform,
  olympiadId,
}: {
  url?: string;
  popupText?: string | null;
  showInform: boolean;
  olympiadId?: string;
}) => {
  if (!url && !showInform) return null;

  return (
    <S.CardActionsGroup>
      {url && (
        <LogClick
          eventType="click"
          eventInfo="olympiad_more_click"
          cd1={olympiadId}
        >
          <SecondaryButton
            type="button"
            size="medium"
            data-testid="olympiad-card-more-info"
            onClick={(e) => {
              stopProp(e);
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
          >
            Подробнее
          </SecondaryButton>
        </LogClick>
      )}
      {showInform && popupText && (
        <S.CardActions onClick={stopProp} onMouseDown={stopProp}>
          <OlympiadInformButton text={popupText} />
        </S.CardActions>
      )}
    </S.CardActionsGroup>
  );
};

const OlympiadCard = ({
  olympiad,
  showWarningMessage,
  hideHeader,
  showInformButton = false,
  onClick,
  'data-testid': dataTestId,
}: OlympiadCardProps) => {
  const { status, dates, url, hint, icon, name, popup_text } = olympiad;
  
  const canShowInform = showInformButton && status === OlympiadStatus.current && !!popup_text;

  const handleCardClick = (e: React.MouseEvent) => {
    if (onClick && !e.defaultPrevented) onClick();
  };

  return (
    <S.OlympiadCardWrap $clickable={!!onClick} onClick={handleCardClick} data-testid={dataTestId}>
      
      <S.CardTopRow>
        <DatesStatus status={status} dates={dates} />
        {hideHeader ? (
          <InsideActions url={url} popupText={popup_text} showInform={canShowInform} olympiadId={olympiad.olympiad_id} />
        ) : (
          hint && <MinistryHintBadge hint={hint} />
        )}
      </S.CardTopRow>

      {!hideHeader && (
        <S.CardHeader>
          <S.CardTitleWrap>
            {icon && <S.Icon src={icon} alt="" />}
            <S.CardTitle>{name}</S.CardTitle>
          </S.CardTitleWrap>
          {canShowInform && (
            <S.CardActions onClick={stopProp} onMouseDown={stopProp}>
              <OlympiadInformButton text={popup_text} />
            </S.CardActions>
          )}
        </S.CardHeader>
      )}

      {showWarningMessage && (
        <S.WarningMessage data-testid="olympiad-card-warning">
          <Info />
          <span>Статистика появится на следующий день после начала олимпиады</span>
        </S.WarningMessage>
      )}

      {hideHeader && hint && (
        <S.HintMessage>
          <span>{hint}</span>
        </S.HintMessage>
      )}
      
    </S.OlympiadCardWrap>
  );
};

export default OlympiadCard;